import { Address } from '@dadohr/shared/domainTypes/address';
import { CustomUserField } from '@dadohr/shared/domainTypes/org';
import { StandardUserFieldKey } from '@dadohr/shared/domainTypes/user';
import { FieldType } from './index';

/**
 * Sanitize user input by removing script tags and event handlers.
 */
export const sanitizeInput = (value: string): string => {
  if (!value) return value;
  return value
    .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
    .replace(/on\w+="[^"]*"/gi, '')
    .replace(/on\w+='[^']*'/gi, '')
    .trim();
};

/**
 * Validate that a value is a valid number (or blank).
 * Returns { valid: true } or { valid: false, error: string }.
 */
export const validateNumber = (
  v: any
): { valid: true } | { valid: false; error: string } => {
  if (v === '' || v === null || v === undefined) return { valid: true };
  const num = Number(v);
  if (isNaN(num)) {
    return { valid: false, error: 'Please enter a valid number' };
  }
  return { valid: true };
};

/**
 * Validate that a value is a valid email (or blank).
 * Returns { valid: true } or { valid: false, error: string }.
 */
export const validateEmail = (
  v: string
): { valid: true } | { valid: false; error: string } => {
  if (!v) return { valid: true };
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(v)) {
    return { valid: false, error: 'Please enter a valid email address' };
  }
  return { valid: true };
};

/**
 * Compute the initial value for the editor based on the current value and field type.
 * If currentValue is set, returns it. Otherwise returns a sensible default.
 */
export const getInitialValue = (
  currentValue: any,
  fieldType: FieldType
): any => {
  if (currentValue !== null && currentValue !== undefined) {
    return currentValue;
  }
  switch (fieldType) {
    case 'date':
    case 'address':
      return null;
    case 'number':
    case 'string':
    case 'relationship':
    case 'email':
    default:
      return '';
  }
};

/**
 * Prepare the value for saving to the database.
 * Handles type-specific transformations (e.g. number conversion, undefined → null).
 */
export const prepareSaveValue = (value: any, fieldType: FieldType): any => {
  let saveValue = value;
  if (fieldType === 'number') {
    saveValue =
      value === '' || value === null || value === undefined
        ? null
        : Number(value);
  }
  return saveValue === undefined ? null : saveValue;
};

/**
 * Build the Firestore update path for a field.
 */
export const buildUpdatePath = (
  fieldKey: string,
  isCustomField: boolean
): string => {
  return isCustomField ? `customFields.${fieldKey}` : fieldKey;
};

/**
 * Check whether an address is completely empty.
 */
export const isAddressEmpty = (address: Address): boolean => {
  return (
    !address.name &&
    !address.addressLine1 &&
    !address.addressLine2 &&
    !address.city &&
    !address.region &&
    !address.postalCode &&
    !address.country
  );
};

/**
 * Determine the field type from a field key and custom field definitions.
 */
export const getFieldType = (
  fieldKey: StandardUserFieldKey | string,
  customUserFields: CustomUserField[]
): FieldType => {
  const customFieldKey = fieldKey.startsWith('customFields.')
    ? fieldKey.replace('customFields.', '')
    : null;

  if (customFieldKey) {
    const customField = customUserFields.find((f) => f.key === customFieldKey);
    if (customField) {
      if (customField.type === 'relationship') return 'relationship';
      if (customField.type === 'date') return 'date';
      if (customField.type === 'number') return 'number';
      if (customField.type === 'address') return 'address';
      return 'string';
    }
  }

  const dateFields: StandardUserFieldKey[] = [
    'hireDate',
    'terminationDate',
    'dateOfBirth'
  ];
  const relationshipFields: StandardUserFieldKey[] = ['managerId'];
  const emailFields: StandardUserFieldKey[] = ['email', 'personalEmail'];
  const addressFields: StandardUserFieldKey[] = ['address'];

  if (dateFields.includes(fieldKey as StandardUserFieldKey)) return 'date';
  if (relationshipFields.includes(fieldKey as StandardUserFieldKey))
    return 'relationship';
  if (emailFields.includes(fieldKey as StandardUserFieldKey)) return 'email';
  if (addressFields.includes(fieldKey as StandardUserFieldKey))
    return 'address';

  return 'string';
};
